// src/modules/units/tyres/shared/tyre-events.js
//
// One shared event target for the tyre subsystem.
//
// THE INVARIANT: CHANGED is emitted only by the tyre WRITE LAYER — tyres.api.js
// for single-tyre writes, and retread-batch.api.js for batch operations, which
// move many tyres at once (dispatch -> AT_RETREAD; a receipt -> In Stock /
// Scrapped / Written Off). Both go through a mutate() wrapper that emits on
// success, so the announcement cannot be forgotten, cannot be skipped on an early
// return, and cannot drift out of step with the write it describes. Views
// subscribe; views never emit. The rule that matters has not loosened: one write
// layer announces and no view ever does — batch writes simply live in a second
// api file because they are a different resource, not because the boundary moved.
//
// Any open view re-renders in response, which is what keeps data fresh without
// each view running its own poll, and is the fix for the "stale until you
// navigate away and back" bug applied at the data layer rather than per screen.
//
// If you find yourself wanting to emit from a view, the write belongs in the API
// layer instead.

const bus = new EventTarget();

export const TYRE_EVENTS = Object.freeze({
  // A WRITE happened that changed one or more tyre RECORDS. Emitted by the tyre
  // write layer only — tyres.api.js (single tyre) and retread-batch.api.js (batch
  // dispatch / receipt / cancel move many at once). See the invariant note above.
  CHANGED: 'tyres:changed', // payload: { externalTyreId?, reason? }

  // The set of YARDS changed — one was created, edited or removed. Its own
  // event because it is a different fact again: the header selector's option list
  // and the Yards view's list must rebuild, but no tyre re-fetch is implied.
  // Emitted by yards.api.js (the yard write layer) — the yard-side parallel to
  // tyres.api.js owning CHANGED. Views subscribe; views never emit it.
  YARDS_CHANGED: 'tyres:yards', // payload: { reason? }

  // The active customer's supplier list changed. Emitted by suppliers.api.js so
  // supplier management and any open supplier dropdown can refresh from one signal.
  SUPPLIERS_CHANGED: 'tyres:suppliers', // payload: { reason?, supplierId? }

  // The active customer's tyre catalogue changed. Emitted by
  // tyre-catalogue.api.js so the catalogue screen and any catalogue-backed
  // typeahead can refresh from one signal.
  CATALOGUE_CHANGED: 'tyres:catalogue', // payload: { reason?, tyreCatalogueEntryId? }

  // A RETREAD BATCH changed — created, edited, dispatched, received or cancelled.
  // Its own event so the batch list and any open batch detail refresh from one
  // signal, the retread-side parallel to YARDS_CHANGED / SUPPLIERS_CHANGED. Emitted
  // by retread-batch.api.js. Dispatch / receipt / cancel ALSO emit CHANGED because
  // they move tyres; a pure draft edit emits only this. Views subscribe; views
  // never emit.
  RETREAD_BATCH_CHANGED: 'tyres:retread-batch', // payload: { reason? }

  // The selected YARD SCOPE changed. Its own event, not CHANGED: a scope change
  // is not a write, and folding it into CHANGED would make every inspection or
  // repair also reset the user's filters. Emitted by tyre-scope.js and by
  // nothing else, so the "one thing owns each announcement" rule still holds —
  // it is just separate owners for separate facts. Views subscribe; views never
  // emit any of them.
  SCOPE_CHANGED: 'tyres:scope' // payload: { yard }  (yard === null means All yards)
});

export function onTyreEvent(type, handler) {
  bus.addEventListener(type, handler);
  return () => bus.removeEventListener(type, handler); // returns an unsubscribe
}

export function emitTyreEvent(type, detail = {}) {
  bus.dispatchEvent(new CustomEvent(type, { detail }));
}
